import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { z } from "zod";

// Source of truth for the Agent Product status contract. Both the public API
// request schemas below and `AgentProductStatus` in `ark` derive from this, so
// the API and the stored type cannot drift.
export const productStatusSchema = z
  .enum(["active", "archived", "draft"])
  .describe("ProductStatus");
export type ProductStatus = z.infer<typeof productStatusSchema>;

export const offerImageSchema = z
  .discriminatedUnion("status", [
    z.object({ status: z.literal("pending") }),
    z.object({
      status: z.literal("ready"),
      imageUrl: z.url({ protocol: /^https?$/ }),
      imageSource: z.enum(["product", "generated"]),
    }),
    z.object({ status: z.literal("failed") }),
  ])
  .describe("OfferImage");

function isReploOrShopifyProductId(id: string) {
  return (
    z.uuid().safeParse(id).success || id.startsWith("gid://shopify/Product/")
  );
}

export const productSuggestionSchema = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.literal("catalog"),
      offerImage: offerImageSchema.optional(),
      title: z.string().min(1),
      productId: z.string().refine(isReploOrShopifyProductId, {
        message: "Expected a Replo product id or a Shopify product GID",
      }),
      imageUrl: z.url({ protocol: /^https?$/ }).nullable(),
      integrationKey: z
        .literal("shopify")
        .optional()
        .describe(
          "Set when the product was read live from the connected Shopify store; its id is a Shopify GID, not a Replo Products id.",
        ),
    }),
    z.object({
      kind: z.literal("website"),
      offerImage: offerImageSchema.optional(),
      title: z.string().min(1),
      sourceUrl: z.url({ protocol: /^https?$/ }),
      productUrl: z.url({ protocol: /^https?$/ }).optional(),
    }),
    z.object({
      kind: z.literal("idea"),
      title: z.string().min(1),
      offerImage: offerImageSchema.optional(),
    }),
  ])
  .describe("ProductSuggestion");

// Source of truth for the Agent Product `type` contract, mirroring the
// `AgentProductType` Postgres enum. UNASSIGNED belongs to the legacy
// `product` table's own enum and is deliberately absent here: the value
// decides whether checkout collects a shipping address, so "undecided" is not
// a state an agent product is allowed to be in.
export const agentProductTypeSchema = z
  .enum([
    "PHYSICAL",
    "SOFTWARE",
    "DIGITAL",
    "MEMBERSHIP",
    "COURSE",
    "SERVICE",
    "EVENT",
    "RETAIL",
  ])
  .describe("AgentProductType");
export type AgentProductType = z.infer<typeof agentProductTypeSchema>;

export const productPathSchema = z
  .object({ productId: z.uuid() })
  .describe("ProductPath");
export type ProductPath = z.infer<typeof productPathSchema>;

export const productQuerySchema = z
  .object({
    titleQuery: z.string().trim().min(1).max(255).optional(),
    // Opaque continuation token: typing a format here would leak the
    // pagination implementation into the public contract.
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
  })
  .describe("ProductQuery");
export type ProductQuery = z.infer<typeof productQuerySchema>;

// NOTE (Yuxin, 2026-08-01): bare z.url() accepts javascript:/data:, which would
// become a stored XSS the moment a checkout link is rendered as an href. The
// protocol option keeps the uri format that the public OpenAPI spec publishes.
export const externalCheckoutUrlSchema = z.url({
  protocol: /^https?$/,
  error: "External checkout link must use HTTP or HTTPS",
});

/**
 * A product's own fields, as opposed to the options, variants, and media that
 * hang off it. Shared so update cannot inherit whatever create grows next —
 * update used to be `productCreateInputSchema.partial()`, which is how a
 * create-only field would have leaked into it.
 */
const productFields = {
  // 1024 matches the agent_product.title / slug VarChar(1024) DB columns.
  title: z.string().trim().min(1).max(1024),
  description: z.string().trim().max(10_000).nullish(),
  slug: z.string().trim().min(1).max(1024).nullish(),
  status: productStatusSchema.optional(),
  externalCheckoutUrl: externalCheckoutUrlSchema.nullish(),
};

export const productCreateInputSchema = z
  .object(productFields)
  .describe("ProductCreateInput");
export type ProductCreateInput = z.infer<typeof productCreateInputSchema>;

export const productUpdateInputSchema = z
  .object(productFields)
  .partial()
  .refine((input) => Object.keys(input).length > 0, {
    message: "At least one field is required.",
  })
  .describe("ProductUpdateInput");
export type ProductUpdateInput = z.infer<typeof productUpdateInputSchema>;

export const productResponseSchema = z
  .object({
    object: z.literal("product"),
    id: z.uuid(),
    title: z.string(),
    description: z.string().nullable(),
    slug: z.string().nullable(),
    status: productStatusSchema,
    type: z.string(),
    externalCheckoutUrl: z.url().nullable(),
  })
  .meta({ id: "Product" });
export type ProductResponse = z.infer<typeof productResponseSchema>;

// Write-side variant addressing: callers that hold no variant ids (the agent
// tool, the public API) name the combination a variant sells. `[]` addresses
// the single-variant case.
export const optionValueRefSchema = z.object({
  optionName: z.string().min(1).max(255).describe('Option name, e.g. "Color".'),
  value: z
    .string()
    .min(1)
    .max(512)
    .describe('Value within that option, e.g. "Navy".'),
});
export type OptionValueRef = z.infer<typeof optionValueRefSchema>;

// Source of truth for per-variant inventory as it rides the wire: the bedrock
// API contract, the SDK, and the ark read shape all derive from these two.
export const agentVariantInventorySchema = z
  .object({
    trackInventory: z.boolean(),
    // Summed stored stock; null when no level row exists yet.
    available: z.number().int().nullable(),
    continueSellingWhenOutOfStock: z.boolean(),
    lowStockThreshold: z.number().int().nullable(),
  })
  .describe("AgentVariantInventory");
export type AgentVariantInventory = z.infer<typeof agentVariantInventorySchema>;

export const agentVariantInventoryPatchSchema = z
  .object({
    trackInventory: z.boolean().optional(),
    // Absolute count; omit to leave the stored level untouched.
    available: z.number().int().min(0).optional(),
    continueSellingWhenOutOfStock: z.boolean().optional(),
    lowStockThreshold: z.number().int().min(0).nullish(),
  })
  .describe("AgentVariantInventoryPatch");
export type AgentVariantInventoryPatch = z.infer<
  typeof agentVariantInventoryPatchSchema
>;

export const productInventoryVariantResponseSchema = z.object({
  optionValues: z.array(optionValueRefSchema),
  ...agentVariantInventorySchema.omit({ lowStockThreshold: true }).shape,
});

export const productInventoryResponseSchema = z
  .object({
    object: z.literal("product_inventory"),
    productId: z.uuid(),
    variants: z.array(productInventoryVariantResponseSchema),
  })
  .meta({ id: "ProductInventory" });
export type ProductInventoryResponse = z.infer<
  typeof productInventoryResponseSchema
>;

// Source of truth for the variant-picker read shape: the bedrock contract, the
// SDK, and the ark read all derive from these three.
export const agentVariantOptionValueSchema = z
  .object({
    name: z.string(),
    value: z.string(),
  })
  .describe("AgentVariantOptionValue");
export type AgentVariantOptionValue = z.infer<
  typeof agentVariantOptionValueSchema
>;

export const agentProductVariantSchema = z
  .object({
    variantId: z.uuid(),
    title: z.string().nullable(),
    sku: z.string().nullable(),
    optionValues: z.array(agentVariantOptionValueSchema),
  })
  .describe("AgentProductVariant");

export const agentProductWithVariantsSchema = z
  .object({
    productId: z.uuid(),
    title: z.string(),
    variants: z.array(agentProductVariantSchema),
  })
  .describe("AgentProductWithVariants");
export type AgentProductWithVariants = z.infer<
  typeof agentProductWithVariantsSchema
>;

// Link products sell through an external checkout URL, not Replo checkout.
export function isLinkProductType(type: AgentProductType): boolean {
  return type === "SERVICE" || type === "SOFTWARE";
}

/**
 * Single source of truth for which variants can track inventory, shared by the
 * bedrock write boundary and the products editor. Only manually fulfilled
 * variants qualify: provider-fulfilled and digital goods are made to order, and
 * link products sell through an external checkout that never sees the stock
 * gate and never decrements. Every product type decides its trackability
 * explicitly — a new enum member is a compile error here until someone
 * chooses, never silently trackable.
 */
export function supportsInventoryTracking({
  productType,
  providerVariantId,
}: {
  productType: AgentProductType;
  providerVariantId: string | null;
}): boolean {
  if (providerVariantId) {
    return false;
  }
  return exhaustiveSwitch(productType)({
    PHYSICAL: true,
    RETAIL: true,
    MEMBERSHIP: true,
    COURSE: true,
    EVENT: true,
    DIGITAL: false,
    SERVICE: false,
    SOFTWARE: false,
  });
}
